import {
  DefaultFetchError,
  FetchCreate,
  FetchDelete,
  FetchGet,
  FetchGetId,
  FetchSuccess,
  FetchUpdate,
  PagingDataResponse,
  PagingParams,
  useFetchCreate,
  useFetchDelete,
  useFetchGet,
  useFetchGetId,
  useFetchUpdate,
} from "../fetch";
import { User } from "../../store/auth";
import {
  ApiSurveyItem,
  SurveyCreateParams,
  SurveysGetParams,
  SurveyStatus,
} from "./surveys";
import { Role } from "../../enums/user";
import { UploadFile } from "antd";

export interface Survey {
  id: string;
  title: string;
  jobId?: string;
  user: User;
  createdAt: string;
  updatedAt: string;
  status: SurveyStatus;
  job: Job;
  submittedDate?: string;
  submittedTimezone?: string;
}

export interface SurveyUpdateParams extends Partial<SurveyCreateParams> {
  id?: string;
}

export interface ResponsesTableRow {
  key: string;
  id: string;
  title: string;
  jobId: string;
  userName: string;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  status: SurveyStatus;
  user?: User;
  job?: Job;
  submittedDate?: string;
  submittedTimezone?: string;
}

export interface SurveysTableData {
  data: ResponsesTableRow[];
  total: number;
}

export interface Job {
  id?: string;
}
export interface SurveyResponseDetails {
  id: string;
  title: string;
  status: SurveyStatus;
  createdAt: string;
  updatedAt: string;
  user: User;
  jobId?: string;
  items: ApiSurveyItem[];
  values: Record<string, any>;
  job?: Job;
  submittedDate?: string;
  submittedTimezone?: string;
}

export const useResponsesGetAll = <DD = SurveysTableData>(
  decorateData?: (data: PagingDataResponse<Survey>) => DD,
): FetchGet<
  PagingDataResponse<Survey>,
  SurveysGetParams,
  DefaultFetchError,
  DD
> =>
  useFetchGet("survey-response", {
    autoStart: false,
    startStateLoading: false,
    decorateData,
  });

export const useResponsesTableGet = () =>
  useResponsesGetAll<SurveysTableData>(
    ({ data, meta }: PagingDataResponse<Survey>): SurveysTableData => ({
      data: data.map(
        (item: Survey): ResponsesTableRow => ({
          key: item.id,
          id: item.id,
          title: item.title,
          jobId: item.jobId || "-",
          job: item.job || "-",
          userName:
            `${item.user?.firstName || ""} ${item.user?.lastName || ""}`.trim() ||
            "-",
          createdById: item.user?.id || "",
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
          status: item.status,
          user: item.user,
          submittedDate: item.submittedDate,
          submittedTimezone: item.submittedTimezone,
        }),
      ),
      total: meta.itemCount,
    }),
  );

export const useResponseGetById = <DD = SurveyResponseDetails>(
  id?: string,
  decorateData?: (data: SurveyResponseDetails) => DD,
): FetchGetId<SurveyResponseDetails, DefaultFetchError, unknown, DD> =>
  useFetchGetId("survey-response", id, {
    autoStart: !!id,
    decorateData,
  });
export interface ResponseUpdateBody {
  items: ResponseItemUpdate[];
  title?: string;
  status?: SurveyStatus;
}

export interface ResponseItemUpdate {
  id?: string;
  sort?: number;
  question?: ResponseQuestionUpdate;
  section?: ResponseSectionUpdate;
}

export interface ResponseQuestionUpdate {
  id: string;
  title?: string;
  type?: string;
  sort?: number;
  options?: any;
  logic?: any[];
  answers?: ResponseAnswerUpdate[];
  value?: ResponseValueItem[];
  fileIds?: string[];
}

export interface ResponseSectionUpdate {
  id: string;
  title?: string;
  sort?: number;
  questions?: ResponseQuestionUpdate[];
}

export interface ResponseAnswerUpdate {
  id: string;
  title?: string;
  sort?: number;
  options?: any;
  logic?: any[];
  fileIds?: string[];
}

export interface ResponseValueItem {
  id: string;
  data: any;
}

export const useResponseUpdate = (
  id?: string,
): FetchUpdate<
  SurveyResponseDetails,
  DefaultFetchError,
  Partial<ResponseUpdateBody>
> => useFetchUpdate("survey-response", id);

export interface FileTag {
  id: string;
  name: string;
}

export interface FileWithMetadata extends UploadFile {
  note?: string;
  tags?: FileTag[];
  id?: string;
}
export interface FileMetadataBody {
  note?: string;
  tags?: FileTag[];
}

export const useFileUpdate = (
  fileId?: string,
): FetchUpdate<
  FileWithMetadata,
  DefaultFetchError,
  Partial<FileMetadataBody>
> => useFetchUpdate("file", fileId);

export interface LoggerUserState {
  email: string;
  id: string;
  role: Role;
}

export interface Logger {
  id: string;
  event: string;
  entity: string;
  action: string;
  entityId: string;
  description?: string;
  data: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  user: User | null;
  before?: LoggerUserState;
  after?: LoggerUserState;
  userTimezone?: string;
}

export interface LoggerTableRow extends Logger {
  key: string;
}

export interface LoggerTableData {
  data: LoggerTableRow[];
  total: number;
}

export interface LoggerGetParams
  extends PagingParams, Partial<Omit<Logger, "id" | "user" | "data">> {
  orderByColumn?: keyof Omit<Logger, "id" | "user" | "data">;
}

export const useLoggersGet = <DD = PagingDataResponse<Logger>>(
  decorateData?: (data: PagingDataResponse<Logger>) => DD,
): FetchGet<
  PagingDataResponse<Logger>,
  LoggerGetParams,
  DefaultFetchError,
  DD
> =>
  useFetchGet("logger", {
    autoStart: false,
    startStateLoading: false,
    decorateData,
    config: {
      params: {
        orderBy: "ASC",
        orderByColumn: "createdAt",
      },
    },
  });

export const useLoggerTableGet = () =>
  useLoggersGet<LoggerTableData>(
    ({ data, meta }: PagingDataResponse<Logger>): LoggerTableData => ({
      data: data.map((item) => ({
        key: item.id,
        ...item,
      })),
      total: meta.itemCount,
    }),
  );

export const useLoggerGetById = <DD = Logger>(
  id?: string,
  decorateData?: (data: Logger) => DD,
): FetchGet<Logger, void, DefaultFetchError, DD> =>
  useFetchGet(`loggers/${id}`, {
    autoStart: false,
    startStateLoading: false,
    decorateData,
  });

export const useResponseDownload = (
  id?: string,
  abortController?: AbortController,
): FetchGetId<ArrayBuffer> =>
  useFetchGetId(
    `job${id ? `/${id}/survey-response/pdf` : ""}`,
    "",
    {
      autoStart: false,
      startStateLoading: false,
      config: {
        headers: { "content-type": "multipart/form-data" },
        signal: abortController?.signal,
      },
    },
    "arraybuffer",
  );
